import {
  ALPHABET,
  ALPHABET_SIZE,
  foldDiacritics,
  letterIndex,
} from "@/lib/tool-kit/ciphers/alphabet";

/**
 * A1Z26: each letter becomes its place in the alphabet, A = 1 … Z = 26.
 *
 * The rules, which the widget states in the same words:
 *
 * - **Encoding.** Letters become numbers joined by the letter separator; each run of
 *   spaces becomes the word separator; line breaks are kept. Digits are dropped and
 *   reported — a 7 in the output would read back as G. With "keep punctuation" on, other
 *   marks (! ? ' : ; and so on) are kept as tokens of their own, except the characters
 *   that also separate numbers (- – — , . ·), which are always dropped.
 * - **Decoding.** Every run of digits is one number. A slash or two or more spaces
 *   start a new word and a line break starts a new line; dashes, commas, full stops
 *   and single spaces just separate numbers; any other punctuation is copied through.
 *   A number with no letter (27, or 0 when counting from 1) becomes "?".
 */

export type A1Z26LetterSeparator = "-" | " " | ",";
export type A1Z26WordSeparator = " / " | "  ";

export interface A1Z26Options {
  letterSeparator: A1Z26LetterSeparator;
  wordSeparator: A1Z26WordSeparator;
  /** Count from Z: Z = 1 … A = 26. */
  reverse: boolean;
  /** Count from 0: A = 0 … Z = 25. */
  zeroBased: boolean;
  /** Encoding: keep punctuation marks as their own tokens. */
  keepPunctuation: boolean;
}

export const DEFAULT_A1Z26_OPTIONS: A1Z26Options = {
  letterSeparator: "-",
  wordSeparator: " / ",
  reverse: false,
  zeroBased: false,
  keepPunctuation: false,
};

const LAST_INDEX = ALPHABET_SIZE - 1;
const LINE_BREAK = /\r?\n/;
const SPACES = /\s+/;
const WORD_BREAK = /\s*\/\s*|\s{2,}/;
const NUMBER_OR_CHAR = /\d+|[^\d]/gu;
const DIGIT = /\p{Nd}/u;
const LETTER = /\p{L}/u;
const WHITESPACE = /\s/u;
/** Characters that separate numbers, so they can never also be kept as punctuation. */
const SEPARATORS = new Set(["-", "–", "—", ",", ".", "·"]);

/** The number a letter (index 0–25) gets under the options. */
export function a1z26Number(index: number, options: A1Z26Options): number {
  const position = options.reverse ? LAST_INDEX - index : index;
  return options.zeroBased ? position : position + 1;
}

/** The letter index (0–25) a number stands for, or null when it stands for none. */
export function a1z26Index(
  value: number,
  options: A1Z26Options
): number | null {
  const position = options.zeroBased ? value : value - 1;
  if (!Number.isInteger(position) || position < 0 || position > LAST_INDEX) {
    return null;
  }
  return options.reverse ? LAST_INDEX - position : position;
}

function isKeptPunctuation(char: string): boolean {
  return !(
    WHITESPACE.test(char) ||
    LETTER.test(char) ||
    DIGIT.test(char) ||
    SEPARATORS.has(char)
  );
}

export interface A1Z26EncodeResult {
  output: string;
  /** Characters that could not be written as numbers: digits and non-A–Z letters. */
  skipped: string[];
}

function encodeWord(
  word: string,
  options: A1Z26Options,
  skipped: Set<string>
): string {
  const tokens: string[] = [];
  for (const char of word) {
    const index = letterIndex(char);
    if (index >= 0) {
      tokens.push(String(a1z26Number(index, options)));
    } else if (DIGIT.test(char) || LETTER.test(char)) {
      skipped.add(char);
    } else if (options.keepPunctuation && isKeptPunctuation(char)) {
      tokens.push(char);
    }
  }
  return tokens.join(options.letterSeparator);
}

export function a1z26Encode(
  text: string,
  options: A1Z26Options
): A1Z26EncodeResult {
  const skipped = new Set<string>();
  const lines = foldDiacritics(text)
    .split(LINE_BREAK)
    .map((line) =>
      line
        .split(SPACES)
        .map((word) => encodeWord(word, options, skipped))
        .filter((word) => word !== "")
        .join(options.wordSeparator)
    );
  return { output: lines.join("\n").trim(), skipped: [...skipped] };
}

export interface A1Z26DecodeResult {
  output: string;
  /** Numbers that stand for no letter under the options, each listed once. */
  invalid: string[];
}

function decodeWord(
  word: string,
  options: A1Z26Options,
  invalid: Set<string>
): string {
  let decoded = "";
  for (const token of word.match(NUMBER_OR_CHAR) ?? []) {
    if (DIGIT.test(token.charAt(0))) {
      const index = a1z26Index(Number.parseInt(token, 10), options);
      if (index === null) {
        invalid.add(token);
        decoded += "?";
      } else {
        decoded += ALPHABET.charAt(index);
      }
    } else if (!(WHITESPACE.test(token) || SEPARATORS.has(token))) {
      decoded += token;
    }
  }
  return decoded;
}

export function a1z26Decode(
  text: string,
  options: A1Z26Options
): A1Z26DecodeResult {
  const invalid = new Set<string>();
  const lines = text.split(LINE_BREAK).map((line) =>
    line
      .split(WORD_BREAK)
      .map((word) => decodeWord(word, options, invalid))
      .filter((word) => word !== "")
      .join(" ")
  );
  return { output: lines.join("\n").trim(), invalid: [...invalid] };
}
